/* eslint-disable no-constant-condition */
import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { redoTreeEdits, setSelectedShapeId, toogleTreeEditingMode, toggleDeleteTreesMode, toggleFlattenAreaMode, Tree, undoTreeEdits } from 'store/slices/TreeSlice';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { clearToolModeHandlers, registerToolModeHandlers } from '../tool/Editor/ToolRegistry';
import { useEffect, useRef } from 'react';
import { selectTreeEditingMode, selectIsFlattenLoading } from 'store/slices/TreeSlice/selector';
import { RootState } from 'store';
import { setThreeDState } from 'store/slices/ToolSlice';
import { isInHouseSimulationMode } from '../../util';
import { selectIsRecomputingFacet } from 'store/slices/DsmShadingSlice/selectors';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';

export default function TreeEditModeBtn(){

	const treeEditingMode = useSelector(selectTreeEditingMode);
	const threeDModelEnabled = useSelector((state: RootState) => state.toolNewPostions.data.threeDModelEnabled);
	const { mode } = useSelector((state: RootState) => state.roofData.data);
	const isAnyToolActionActive = useSelector(selectIsAnyToolActionActive);
	const disableWhenOtherAction = (!treeEditingMode && !!isAnyToolActionActive);
	const dispatch = useDispatch();
	const queriesRunningInBackground = useQueriesRunningInBackGround();
	const isDisabled = useSelector(selectIsRecomputingFacet) || queriesRunningInBackground || disableWhenOtherAction;
	const treeEditingModeRef = useRef(treeEditingMode);
	const isSupportedMode = isInHouseSimulationMode(mode);

	const isFlattenLoading = useSelector(selectIsFlattenLoading);
	const isFlattenLoadingRef = useRef(isFlattenLoading);
	useEffect(() => { isFlattenLoadingRef.current = isFlattenLoading; }, [isFlattenLoading]);

	function handleClick(){
		if (treeEditingMode) {
			dispatch(toggleDeleteTreesMode(false));
			dispatch(toggleFlattenAreaMode(false));
			dispatch(setSelectedShapeId(null));
			dispatch(toogleTreeEditingMode(false));
			if (threeDModelEnabled) {
				dispatch(setThreeDState({ enabled: false }));
			}
			return;
		}
		dispatch(toogleTreeEditingMode(true));
	}

	// useEffect(() => {
	// 	if (treeEditingMode && !wasEditingRef.current) {
	// 		prevCirclesRef.current = circles;
	// 	}
	// 	wasEditingRef.current = treeEditingMode;
	// }, [treeEditingMode, circles]);
	useEffect(() => {
		treeEditingModeRef.current = treeEditingMode;
	}, [treeEditingMode]);

	useEffect(() => {
		if (isSupportedMode) return;
		if (!treeEditingMode) return;
		dispatch(toggleDeleteTreesMode(false));
		dispatch(toggleFlattenAreaMode(false));
		dispatch(setSelectedShapeId(null));
		dispatch(toogleTreeEditingMode(false));
		if (threeDModelEnabled) {
			dispatch(setThreeDState({ enabled: false }));
		}
	}, [dispatch, isSupportedMode, threeDModelEnabled, treeEditingMode]);

	useEffect(() => {
		const isEditableTarget = (target: EventTarget | null) => {
			if (!(target instanceof HTMLElement)) return false;
			const tag = target.tagName;
			return (
				tag === 'INPUT' ||
				tag === 'TEXTAREA' ||
				tag === 'SELECT' ||
				target.isContentEditable
			);
		};

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.defaultPrevented) return;
			if (!treeEditingModeRef.current) return;
			if (!(e.ctrlKey || e.metaKey)) return;

			const key = e.key.toLowerCase();
			const activeEl = document.activeElement as HTMLElement | null;

			const isTyping =
				activeEl &&
				(activeEl.tagName === 'INPUT' ||
					activeEl.tagName === 'TEXTAREA' ||
					activeEl.isContentEditable);

			if (isTyping) return;
			if (isFlattenLoadingRef.current) return;

			const isUndo = key === 'z' && !e.shiftKey;
			const isRedo = key === 'y' || (key === 'z' && e.shiftKey);

			if (isUndo) {
				e.preventDefault();
				dispatch(undoTreeEdits());
				return;
			}

			if (isRedo) {
				e.preventDefault();
				dispatch(redoTreeEdits());
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [dispatch]);
	useEffect(() => {
		const cancelHandler = () => {
			// dispatch(setCircles(prevCirclesRef.current));
			dispatch(toggleDeleteTreesMode(false));
			dispatch(toggleFlattenAreaMode(false));
			dispatch(setSelectedShapeId(null));
			dispatch(toogleTreeEditingMode(false));
			if (threeDModelEnabled) {
				dispatch(setThreeDState({ enabled: false }));
			}
		};
		const doneHandler = () => {
			dispatch(toggleDeleteTreesMode(false));
			dispatch(toggleFlattenAreaMode(false));
			dispatch(setSelectedShapeId(null));
			dispatch(toogleTreeEditingMode(false));
			if (threeDModelEnabled) {
				dispatch(setThreeDState({ enabled: false }));
			}
		};
		registerToolModeHandlers(TOOL_ACTION.EDIT_TREE, {
			cancel: cancelHandler,
			done: doneHandler,
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.EDIT_TREE);
		};
	}, [dispatch, threeDModelEnabled]);

	const iconColor = treeEditingMode ? 'var(--labelValueColor)' : (isDisabled ? '#9AA3A8' : 'var(--defaultIconColor)');

	if (!isSupportedMode) return null;

	return (
		<Button
			id='edit-trees'
			className={`${treeEditingMode ? 'active' : ''} btn-tools tree-tool-btns`}
			style={{
				background: `${treeEditingMode ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
				color: `${treeEditingMode ? 'var(--labelValueColor)' : (isDisabled ? '#9AA3A8' : 'var(--labelValueColor)')}`,
			}}
			disabled={isDisabled}
			onClick={handleClick}
			tooltipContent='Edit Trees'
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none">
				<g clipPath="url(#clip0_3909_8)">
					<path d="M10.3615 22.9575V8.83367M13.5434 17.5253H14.604C15.9191 17.5264 
					17.1876 17.0269 18.1634 16.1239C19.1392 15.2209 19.7527 13.9787 19.8847 
					12.6384C20.0167 11.2982 19.6579 9.95548 18.8779 8.87096C18.0979 7.78645 
					16.9524 7.03747 15.6636 6.76941C15.6636 3.76864 13.5434 1.22852 10.3615 
					1.22852C7.17954 1.22852 5.05932 3.76864 5.05932 6.76941C3.77459 7.04201 2.63405 
					7.79255 1.85793 8.8761C1.08181 9.95966 0.725173 11.2994 0.856897 12.6365C0.98862 
					13.9736 1.59936 15.2133 2.57116 16.1162C3.54297 17.0191 4.80691 17.5211 6.1189 
					17.5253H7.17954M10.3615 15.3524L13.0131 12.6362M10.3615 13.1795L7.70986 10.4633M8.24018 
					22.9575H12.4827" 
					stroke={`${treeEditingMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} 
					strokeWidth="1.37" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M10.3615 22.9575V8.83367M13.5434 17.5253H14.604C15.9191 17.5264 17.1876 17.0269 
					18.1634 16.1239C19.1392 15.2209 19.7527 13.9787 19.8847 12.6384C20.0167 11.2982 19.6579 
					9.95548 18.8779 8.87096C18.0979 7.78645 16.9524 7.03747 15.6636 6.76941C15.6636 3.76864 
					13.5434 1.22852 10.3615 1.22852C7.17954 1.22852 5.05932 3.76864 5.05932 6.76941C3.77459 
					7.04201 2.63405 7.79255 1.85793 8.8761C1.08181 9.95966 0.725173 11.2994 0.856897 
					12.6365C0.98862 13.9736 1.59936 15.2133 2.57116 16.1162C3.54297 17.0191 4.80691 17.5211 
					6.1189 17.5253H7.17954M10.3615 15.3524L13.0131 12.6362M10.3615 13.1795L7.70986 
					10.4633M8.24018 22.9575H12.4827" 
					stroke={`${treeEditingMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.37" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M20.4565 17.6736L22.0835 19.3402M18.2872 23.2289L22.8346 18.5541C23.0482 
					18.3353 23.1682 18.0386 23.1682 17.7292C23.1682 17.4198 23.0482 17.123 22.8346 
					16.9042C22.621 16.6854 22.3313 16.5625 22.0293 16.5625C21.7272 16.5625 21.4375 16.6854 
					21.2239 16.9042L16.6602 21.5623V23.2289H18.2872Z" 
					stroke={`${treeEditingMode ? 'var(--primary)' : 'var(--defaultIconColor)'}`} 
					strokeLinecap="round" strokeLinejoin="round"/>
				</g>
				<defs>
					<clipPath id="clip0_3909_8">
						<rect width="24" height="24" fill="white"/>
					</clipPath>
				</defs>
			</svg>
		</Button>
	);
}
